import { useSupabaseQuery, useSupabaseRow } from './useSupabaseQuery';
import type { Code, CodeRedemption, LedgerTransaction } from '../types/database';

export function useCodes(filters?: {
	status?: string;
	search?: string;
	limit?: number;
	offset?: number;
}) {
	const limit = filters?.limit ?? 50;
	const offset = filters?.offset ?? 0;
	return useSupabaseQuery<Code>(
		(client) => {
			let q = client
				.from('codes')
				.select('*', { count: 'exact' })
				.order('created_at', { ascending: false })
				.range(offset, offset + limit - 1);
			if (filters?.status) {
				q = q.eq('status', filters.status);
			}
			if (filters?.search) {
				q = q.ilike('code', `%${filters.search}%`);
			}
			return q;
		},
		[filters?.status, filters?.search, limit, offset]
	);
}

export function useCode(id: string | undefined) {
	return useSupabaseRow<Code>('codes', id);
}

export function useCodeRedemptions(codeId: string | undefined) {
	return useSupabaseQuery<CodeRedemption & { users: { phone_number: string; display_name: string | null } | null; orders: { id: string; status: string } | null }>(
		(client) =>
			client
				.from('code_redemptions')
				.select('*, users(phone_number, display_name), orders(id, status)')
				.eq('code_id', codeId ?? '')
				.order('created_at', { ascending: false }),
		[codeId]
	);
}

export function useLedgerTransactions(filters: {
	userId?: string;
	orderId?: string;
	codeId?: string;
}) {
	return useSupabaseQuery<LedgerTransaction & { users: { phone_number: string; display_name: string | null } | null }>(
		(client) => {
			let q = client
				.from('ledger_transactions')
				.select('*, users(phone_number, display_name)')
				.order('created_at', { ascending: false });
			if (filters.userId) {
				q = q.eq('user_id', filters.userId);
			}
			if (filters.orderId) {
				q = q.eq('order_id', filters.orderId);
			}
			if (filters.codeId) {
				q = q.eq('code_id', filters.codeId);
			}
			return q;
		},
		[filters.userId, filters.orderId, filters.codeId]
	);
}

export function useUserBalance(userId: string | undefined) {
	return useSupabaseQuery<{ currency: string; amount: number }>(
		(client) =>
			client
				.from('ledger_transactions')
				.select('currency, amount')
				.eq('user_id', userId ?? ''),
		[userId]
	);
}
